/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import moment from 'moment';
import i18n from '../i18n';

// Component imports
import { ApartmentOutlined, CommentOutlined, CustomerServiceOutlined, DatabaseOutlined, GlobalOutlined, LineChartOutlined, MailOutlined, MessageOutlined, NotificationOutlined, SyncOutlined, UnorderedListOutlined, VideoCameraOutlined, WalletOutlined } from '@ant-design/icons';
import { HelviaIcon } from '../components/Icons/HelviaIcon';
import { KibanaIcon } from '../components/Icons/KibanaIcon';
import { MongoIcon } from '../components/Icons/MongoIcon';
import { GearIcon } from '../components/Icons/GearIcon';
import { TeamsIcon } from '../components/Icons/platformIcons/Teams';
import { FacebookIcon } from '../components/Icons/platformIcons/Facebook';
import { FacebookMessengerIcon } from '../components/Icons/platformIcons/FacebookMessenger';
import { UnityIcon } from '../components/Icons/platformIcons/Unity';
import { ViberIcon } from '../components/Icons/platformIcons/Viber';
import { HrwizIcon } from '../components/Icons/HrwizIcon';
import { SlackIcon } from '../components/Icons/platformIcons/Slack';
import { WebchatIcon } from '../components/Icons/platformIcons/Webchat';
import { ApiIcon } from '../components/Icons/platformIcons/Api';
import { GreekFlagIcon } from '../components/Icons/countryFlags/GreekFlagIcon';
import { EnglishFlagIcon } from '../components/Icons/countryFlags/EnglishFlagIcon';
import { FrenchFlagIcon } from '../components/Icons/countryFlags/FrenchFlagIcon';
import { GermanFlagIcon } from '../components/Icons/countryFlags/GermanFlagIcon';
import { ItalianFlagIcon } from '../components/Icons/countryFlags/ItalianFlagIcon';
import { SpanishFlagIcon } from '../components/Icons/countryFlags/SpanishFlagIcon';
import { PortugueseFlagIcon } from '../components/Icons/countryFlags/PortugueseFlagIcon';
import { InstagramIcon } from '../components/Icons/platformIcons/Instagram';
import { BulgarianFlagIcon } from '../components/Icons/countryFlags/BulgarianFlagIcon';
import { ChineseFlagIcon } from '../components/Icons/countryFlags/ChineseFlagIcon';
import { CzechFlagIcon } from '../components/Icons/countryFlags/CzechFlagIcon';
import { DutchFlagIcon } from '../components/Icons/countryFlags/DutchFlagIcon';
import { EstonianFlagIcon } from '../components/Icons/countryFlags/EstonianFlagIcon';
import { FinnishFlagIcon } from '../components/Icons/countryFlags/FinnishFlagIcon';
import { HebrewFlagIcon } from '../components/Icons/countryFlags/HebrewFlagIcon';
import { PolishFlagIcon } from '../components/Icons/countryFlags/PolishFlagIcon';
import { RomanianFlagIcon } from '../components/Icons/countryFlags/RomanianFlagIcon';
import { PhilippinesFlagIcon } from '../components/Icons/countryFlags/PhilippinesFlagIcon';
import { RussianFlagIcon } from '../components/Icons/countryFlags/RussianFlagIcon';
import { SerbianFlagIcon } from '../components/Icons/countryFlags/SerbianFlagIcon';
import { SlovenianFlagIcon } from '../components/Icons/countryFlags/SlovenianFlagIcon';
import { SwedishFlagIcon } from '../components/Icons/countryFlags/SwedishFlagIcon';
import { TurkishFlagIcon } from '../components/Icons/countryFlags/TurkishFlagIcon';
import { UkrainianFlagIcon } from '../components/Icons/countryFlags/UkrainianFlagIcon';
import { CroatianFlagIcon } from '../components/Icons/countryFlags/CroatianFlagIcon';
import { DanishFlagIcon } from '../components/Icons/countryFlags/DanishFlagIcon';
import { HungarianFlagIcon } from '../components/Icons/countryFlags/HungarianFlagIcon';
import { NorwegianFlagIcon } from '../components/Icons/countryFlags/NorwegianFlagIcon';
import { WhatsAppIcon } from '../components/Icons/platformIcons/WhatsAppIcon';

export const LABS = {
  ORG: {
    key: 'org',
    label: i18n.t('Organization')
  },
  BOT: {
    key: 'bot',
    label: i18n.t('Agent')
  },
  OBSERVATORY: {
    key: 'observatory',
    label: i18n.t('Observatory')
  },
  LIVECHAT: {
    key: 'livechat',
    label: i18n.t('LiveChat')
  }
};

export const WEBPAGE_TITLE_DEFAULT = 'Helvia Console';

// Link to webiste
export const HELVIA_WEBSITE_URL = 'https://helvia.ai/';

export const DEFAULT_PAGINATION_SIZE = 10;

// Auth
export const REFRESH_TOKEN_INTERVAL_MILLISECONDS = 60 * 60 * 1000; // 1 hour

// Media
export const MAX_UPLOAD_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

// User permissions
export const PERMISSIONS_ORG = {
  ADMIN: 'HBF_ORG_ADMIN',         // Can create/delete bots, enable org-wide integrations
  EDITOR: 'HBF_ORG_EDITOR',       // Has the HBF_BOT_EDITOR editor role on all bots of the organization
  VIEWER: 'HBF_ORG_VIEWER'        // Has the HBF_BOT_VIEWER editor role on all bots of the organization
};
export const PERMISSIONS_TENANT = {
  ADMIN: 'HBF_TENANT_ADMIN',      // Can see and edit all available powerups
  EDITOR: 'HBF_TENANT_EDITOR',    // Similar to content-editors. Can see CONTENT and ANALYTICS powerups and edit them
  VIEWER: 'HBF_TENANT_VIEWER'     // Can see CONTENT and ANALYTICS powerups but not edit them
};

export const ADDITIONAL_ROLES_ORG = {
  LIVE_AGENT: 'HBF_ORG_LIVE_AGENT',         // Can conversate on the Live-Chat platform
  LIVECHAT_ADMIN: 'HBF_ORG_LIVECHAT_ADMIN'  // Can edit the settings of the Live-Chat platform
};

export const ADDITIONAL_ROLES_TENANT = {
  LIVECHAT_ADMIN: 'HBF_TENANT_LIVECHAT_ADMIN',  // Can edit the settings of the Live-Chat platform for that tenant
  LIVE_AGENT: 'HBF_TENANT_LIVE_AGENT'
};

// Common permission groups
export const PERMISSIONS_GROUP_ADMIN = [
  PERMISSIONS_ORG.ADMIN,
  PERMISSIONS_TENANT.ADMIN
];
export const PERMISSIONS_GROUP_EDITOR = [
  ...PERMISSIONS_GROUP_ADMIN,
  PERMISSIONS_ORG.EDITOR,
  PERMISSIONS_TENANT.EDITOR
];
export const PERMISSIONS_GROUP_VIEWER = [
  ...PERMISSIONS_GROUP_ADMIN,
  ...PERMISSIONS_GROUP_EDITOR,
  PERMISSIONS_ORG.VIEWER,
  PERMISSIONS_TENANT.VIEWER
];

// A map to the powerups that we can receive from the backend
export const POWERUPS = {
  AUTOMATED_ANSWERS: {
    key: 'FAQS',
    icon: <MessageOutlined />
  },
  FLOWS: {
    key: 'FLOWS',
    icon: <ApartmentOutlined />
  },
  SURVEYS: {
    key: 'SURVEYS',
    icon: <UnorderedListOutlined />
  },
  CHAT_SESSIONS: {
    key: 'CHAT_SESSIONS',
    icon: <CommentOutlined />
  },
  ANALYTICS: {
    key: 'ANALYTICS',
    icon: <LineChartOutlined />
  },
  BROADCASTS: {
    key: 'BROADCASTS',
    icon: <NotificationOutlined />
  },
  MONITORING: {
    key: 'MONITORING',
    icon: <VideoCameraOutlined />
  },
  BILLING: {
    key: 'BILLING',
    icon: <WalletOutlined />
  },
  HRWIZ: {
    key: 'HRWIZ',
    icon: <HrwizIcon />
  },
  BACKUPS: {
    key: 'BACKUPS',
    icon: <DatabaseOutlined />
  },
  SETTINGS: {
    key: 'SETTINGS',
    icon: <GearIcon />
  },
  NLP_CACHING: {
    key: 'NLU_CACHING',
    icon: <SyncOutlined />
  }
};

// Authentication methods for user login
export const AUTHENTICATION_METHODS = {
  EMAIL: 'EMAIL',
  GOOGLE: 'GOOGLE',
  MICROSOFT: 'MICROSOFT',
  TEAMS: 'TEAMS',
  INTEGRATION: 'INTEGRATION'
};

// Local storage data - authentication related (will be cleared on logout)
export const TEMP_STORAGE_KEYS = {
  USER: 'hbf/user',
  TOKEN: 'hbf/token',
  REFRESH_TOKEN: 'hbf/rfr',
  ORGANIZATION_ID: 'hbf/orgId',
  RECENT_BOTS: 'hbf/recentBots',
  OBSERVATORY_SEARCH_PARAMS: 'hbf/observatorySearchParams'
};

// Local storage data - misc (will NOT be cleared on logout)
export const STORAGE_KEYS = {
  HRWIZ_TENANT_ID: 'hbf/hrwiztenantId',
  TEAMS_APPLICATION: 'hbf/teamsapp',
  IS_SIDEBAR_OPEN: 'hbf/isSidebarOpen',
  LIVECHAT_PUSH_NOTIFICATIONS: 'hbf/pushNotifications',
  LIVECHAT_SEND_MESSAGE_MODE: 'hbf/sendMessageMode',
  LIVECHAT_CONVERSATIONS_SOUND: 'hbf/isConversationsSoundEnabled',
  LIVECHAT_FAVORITE_AUTOMATIONS: 'hbf/favoriteAutomations',
  LIVECHAT_REQUESTS_SOUND: 'hbf/isRequestsSoundEnabled',
  LIVECHAT_LAYOUT_PREFERENCES: 'hbf/livechatLayout',
  LIVECHAT_SPELL_CHECK_LANGUAGE: 'hbf/livechatSpellCheckLanguage'
};

export const TEAMS_APPLICATIONS = {
  HRWIZ: 'HRwiz',
  LIVECHAT: 'LiveChat'
};

export const MEDIA_LIBRARY_VIEWS = {
  GRID: 'grid',
  TABLE: 'table'
};

export const BROADCAST_TYPES = {
  MESSAGE: 'MESSAGE',
  POLL: 'POLL',
  QUIZ: 'QUIZ'
};

export const BROADCAST_STATUSES = {
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
  SENDING: 'SENDING',
  PENDING: 'PENDING',
  WAITING: 'WAITING'
};

export const CHAPTERSOURCE_SCHEMAS = {
  FLOWGRAPH: 'FLOWGRAPH',
  AUTOMATED_ANSWER: 'FAQLITE'
};

export const ACTIVITY_TYPES = {
  DECISION_TREE: 'DECISION_TREE',
  LIVECHAT_FLOW: 'LIVECHAT_FLOW',
  INTERVIEW: 'INTERVIEW',
  AUTOMATED_ANSWERS: 'AUTOMATED_ANSWERS',
  KNOWLEDGE_BASE_ARTICLE: 'KNOWLEDGE_BASE_ARTICLE',
  LIVECHAT_SYSTEM_MESSAGE: 'LIVECHAT_SYSTEM_MESSAGE',
  USER_FEEDBACK: 'USER_FEEDBACK'
};

export const ACTIVITY_TYPES_LABELS = {
  DECISION_TREE: 'Decision Tree',
  LIVECHAT_FLOW: 'Livechat Flow',
  INTERVIEW: 'Interview',
  AUTOMATED_ANSWERS: 'Intent',
  KNOWLEDGE_BASE_ARTICLE: 'Article',
  LIVECHAT_SYSTEM_MESSAGE: 'Livechat System Message',
  USER_FEEDBACK: 'User Feedback'
};

export const KNOWLEDGE_BASE_TYPES = {
  INTERNAL: 'INTERNAL',
  INTEGRATION: 'INTEGRATION',
  SHARED: 'SHARED'
};

export const KNOWLEDGE_BASE_SPACES = {
  EX: 'EX',
  CX: 'CX'
};

// Weekdays mapping (for handover business hours)
export const WEEKDAYS = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 0
};

export const PLATFORMS = {
  WEBCHAT: {
    key: 'MICROSOFT_WEB_CHAT',
    icon: <GlobalOutlined />
  },
  TEAMS: {
    key: 'MICROSOFT_TEAMS',
    icon: <TeamsIcon />
  },
  VIBER: {
    key: 'VIBER',
    icon: <ViberIcon />
  },
  FACEBOOK: {
    key: 'FACEBOOK',
    icon: <FacebookIcon />
  },
  EMAIL: {
    key: 'EMAIL',
    icon: <MailOutlined />
  },
  LIVECHAT: {
    key: 'LIVECHAT',
    icon: <CustomerServiceOutlined />
  }
};

export const MEDIA_UPLOAD_TYPES = {
  JPG: 'image/jpg',
  JPEG: 'image/jpeg',
  PNG: 'image/png',
  GIF: 'image/gif',
  MP4: 'video/mp4',
  MPEG: 'audio/mpeg'
};

export const DATA_RETENTION_OPTIONS = [
  {
    value: 1,
    label: i18n.t('countMonths', { count: 1 })
  },
  {
    value: 2,
    label: i18n.t('countMonths', { count: 2 })
  },
  {
    value: 3,
    label: i18n.t('countMonths', { count: 3 })
  }, {
    value: 4,
    label: i18n.t('countMonths', { count: 4 })
  },
  {
    value: 5,
    label: i18n.t('countMonths', { count: 5 })
  }, {
    value: 6,
    label: i18n.t('countMonths', { count: 6 })
  },
  {
    value: 7,
    label: i18n.t('countMonths', { count: 7 })
  },
  {
    value: 8,
    label: i18n.t('countMonths', { count: 8 })
  }, {
    value: 9,
    label: i18n.t('countMonths', { count: 9 })
  },
  {
    value: 10,
    label: i18n.t('countMonths', { count: 10 })
  },
  {
    value: 11,
    label: i18n.t('countMonths', { count: 11 })
  },
  {
    value: 12,
    label: i18n.t('countMonths', { count: 12 })
  },
  {
    value: 13,
    label: i18n.t('countMonths', { count: 13 })
  },
  {
    value: 14,
    label: i18n.t('countMonths', { count: 14 })
  }, {
    value: 15,
    label: i18n.t('countMonths', { count: 15 })
  }, {
    value: 16,
    label: i18n.t('countMonths', { count: 16 })
  }, {
    value: 17,
    label: i18n.t('countMonths', { count: 17 })
  }, {
    value: 18,
    label: i18n.t('countMonths', { count: 18 })
  }, {
    value: 19,
    label: i18n.t('countMonths', { count: 19 })
  }, {
    value: 20,
    label: i18n.t('countMonths', { count: 20 })
  }, {
    value: 21,
    label: i18n.t('countMonths', { count: 21 })
  }, {
    value: 22,
    label: i18n.t('countMonths', { count: 22 })
  }, {
    value: 23,
    label: i18n.t('countMonths', { count: 23 })
  }, {
    value: 24,
    label: i18n.t('countMonths', { count: 24 })
  }
];

export const AUDIT_LOG_RETENTION_OPTIONS = [
  {
    value: 1,
    label: '1 month'
  },
  {
    value: 2,
    label: '2 months'
  },
  {
    value: 3,
    label: '3 months'
  }
];

//Keep alphabetical order when adding new languages
export const LANGUAGE_OPTIONS = [
  {
    key: 'pt-br',
    label: 'Brazilian Portuguese',
    icon: <PortugueseFlagIcon />
  },
  {
    key: 'bg',
    label: 'Bulgarian',
    icon: <BulgarianFlagIcon />
  },
  {
    key: 'zh',
    label: 'Chinese',
    icon: <ChineseFlagIcon />
  },
  {
    key: 'hr',
    label: 'Croatian',
    icon: <CroatianFlagIcon />
  },
  {
    key: 'cs',
    label: 'Czech',
    icon: <CzechFlagIcon />
  },
  {
    key: 'da',
    label: 'Danish',
    icon: <DanishFlagIcon />
  },
  {
    key: 'nl',
    label: 'Dutch',
    icon: <DutchFlagIcon />
  },
  {
    key: 'en',
    label: 'English',
    icon: <EnglishFlagIcon />
  },
  {
    key: 'et',
    label: 'Estonian',
    icon: <EstonianFlagIcon />
  },
  {
    key: 'fil',
    label: 'Filipino',
    icon: <PhilippinesFlagIcon />
  },
  {
    key: 'fi',
    label: 'Finnish',
    icon: <FinnishFlagIcon />
  },
  {
    key: 'fr',
    label: 'French',
    icon: <FrenchFlagIcon />
  },
  {
    key: 'de',
    label: 'German',
    icon: <GermanFlagIcon />
  },
  {
    key: 'el',
    label: 'Greek',
    icon: <GreekFlagIcon />
  },
  {
    key: 'he',
    label: 'Hebrew',
    icon: <HebrewFlagIcon />
  },
  {
    key: 'hu',
    label: 'Hungarian',
    icon: <HungarianFlagIcon />
  },
  {
    key: 'it',
    label: 'Italian',
    icon: <ItalianFlagIcon />
  },
  {
    key: 'no',
    label: 'Norwegian',
    icon: <NorwegianFlagIcon />
  },
  {
    key: 'pl',
    label: 'Polish',
    icon: <PolishFlagIcon />
  },
  {
    key: 'pt',
    label: 'Portuguese',
    icon: <PortugueseFlagIcon />
  },
  {
    key: 'ro',
    label: 'Romanian',
    icon: <RomanianFlagIcon />
  },
  {
    key: 'ru',
    label: 'Russian',
    icon: <RussianFlagIcon />
  },
  {
    key: 'sr',
    label: 'Serbian',
    icon: <SerbianFlagIcon />
  },
  {
    key: 'sl',
    label: 'Slovenian',
    icon: <SlovenianFlagIcon />
  },
  {
    key: 'es',
    label: 'Spanish',
    icon: <SpanishFlagIcon />
  },
  {
    key: 'sv',
    label: 'Swedish',
    icon: <SwedishFlagIcon />
  },
  {
    key: 'tr',
    label: 'Turkish',
    icon: <TurkishFlagIcon />
  },
  {
    key: 'uk',
    label: 'Ukrainian',
    icon: <UkrainianFlagIcon />
  }
];

// Used for spell checking in the rich text editor
export const SPELLCHECK_LANGUAGES = [
  {
    label: 'English',
    key: 'en-US'
  },
  {
    label: 'German',
    key: 'de-DE'
  },
  {
    label: 'Spanish',
    key: 'es-ES'
  },
  {
    label: 'French',
    key: 'fr-FR'
  },
  {
    label: 'Greek',
    key: 'el-GR'
  },
  {
    label: 'Dutch',
    key: 'nl-NL'
  },
  {
    label: 'Portuguese',
    key: 'pt-PT'
  },
  {
    label: 'Polish',
    key: 'pl-PL'
  },
  {
    label: 'Romanian',
    key: 'ro-RO'
  },
  {
    label: 'Russian',
    key: 'ru-RU'
  },
  {
    label: 'Slovenian',
    key: 'sl-SI'
  },
  {
    label: 'Swedish',
    key: 'sv-SE'
  },
  {
    label: 'Ukrainian',
    key: 'uk-UA'
  }
];

export const NLP_OPENAI_MODEL_CATEGORIES = {
  COMPLETION: {
    key: 'COMPLETION',
    label: 'Completion'
  },
  CHAT: {
    key: 'CHAT',
    label: 'Chat'
  }
};

export const NLP_OPENAI_TRAINING_TYPES = {
  ZERO_SHOT: {
    key: 'ZERO_SHOT',
    label: 'Zero shot'
  },
  ONE_SHOT: {
    key: 'ONE_SHOT',
    label: 'One shot'
  },
  FEW_SHOT: {
    key: 'FEW_SHOT',
    label: 'Few shot'
  },
  CUSTOM_PROMPT: {
    key: 'CUSTOM_PROMPT',
    label: 'Custom prompt'
  }
};

export const NLP_OPENAI_CHAT_MESSAGES_ROLES = {
  USER: {
    key: 'user',
    label: 'User'
  },
  ASSISTANT: {
    key: 'assistant',
    label: 'Assistant'
  }
};

export const NLP_PIPELINE_STATUSES = {
  INITIALIZING: 'INITIALIZING',
  CREATED: 'CREATED',
  READY: 'READY',
  SYNCING: 'SYNCING',
  TRAINING: 'TRAINING',
  OUTDATED: 'OUTDATED',
  FAILED: 'FAILED'
};

export const EMBEDDING_PROVIDERS = {
  OPEN_AI: 'OPENAI',
  AZURE: 'AZURE_OPENAI'
};

export const TEXT_GENERATION_PROVIDERS = {
  OPEN_AI: 'OPENAI',
  AZURE_OPENAI: 'AZURE_OPENAI',
  ANYSCALE: 'ANYSCALE',
  GOOGLE_GENAI: 'GOOGLE_GENAI'
};

export const TRANSLATION_PROVIDERS = {
  GOOGLE: 'GOOGLE',
  AZURE: 'AZURE'
};

export const NLP_TYPES = {
  DIALOGFLOW_NLP: 'DIALOGFLOW_NLP',
  WIT: 'WIT', // Hidden for now
  LUIS_NLP: 'LUIS_NLP',
  LEX: 'AMAZONLEX', // Hidden for now
  OPENAI_NLP: 'OPENAI_NLP',
  HELVIA_GPT: 'HELVIA_GPT',
  HELVIA_NLP_SPECIFICATION: 'HELVIA_NLP_SPECIFICATION',
  HELVIA_RAG_PIPELINE: 'HELVIA_RAG_PIPELINE'
};

export const DIALOGFLOW_REGIONS = {
  EU_W1: 'Europe-West1',
  EU_W2: 'Europe-West2',
  AU_SE1: 'Australia-Southeast1',
  AS_NE1: 'Asia-Northeast1',
  GLOBAL: 'Global',
  US: 'US'
};

export const USER_INVITATION_STATUSES = {
  ACCEPTED: 'ACCEPTED',
  PENDING: 'PENDING',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED'
};

export const WEBCHAT_WIDGET_MODES = {
  BUBBLE: 'BUBBLE',
  EMBEDDED: 'EMBEDDED'
};

// Taken from https://uptimerobot.com/api/
export const MONITORING_STATUSES = {
  PAUSED: 0,
  NOT_YET_CHECKED: 1,
  UP: 2,
  SEEMS_DOWN: 8,
  DOWN: 9
};

export const DEFAULT_TENANT_PLATFORM = PLATFORMS.TEAMS.key;

export const BOT_DEPLOYMENTS_CHANNELS = {
  MICROSOFT_TEAMS: {
    key: 'MICROSOFT_TEAMS',
    order: 2,
    icon: <TeamsIcon/>,
    name: 'Microsoft Teams',
    description: i18n.t('Deploy your Agent on Microsoft Teams', { context: 'AI agent' }),
    isPreviewAvailable: false
  },
  MICROSOFT_WEB_CHAT: {
    key: 'MICROSOFT_WEB_CHAT',
    order: 1,
    icon: <WebchatIcon/>,
    name: 'Webchat',
    description: i18n.t('Deploy your Agent on your website', { context: 'AI agent' }),
    isPreviewAvailable: true
  },
  VIBER: {
    key: 'VIBER',
    order: 4,
    icon: <ViberIcon/>,
    name: 'Viber',
    description: i18n.t('Deploy your Agent on Viber', { context: 'AI agent' }),
    isPreviewAvailable: false
  },
  FACEBOOK: {
    key: 'FACEBOOK',
    order: 3,
    icon: <FacebookMessengerIcon/>,
    name: 'Facebook',
    description: i18n.t('Deploy your Agent on Messenger', { context: 'AI agent' }),
    isPreviewAvailable: true
  },
  UNITY: {
    key: 'UNITY',
    order: 5,
    icon: <UnityIcon />,
    name: 'Unity',
    description: i18n.t('Deploy your Agent on Unity', { context: 'AI agent' }),
    isPreviewAvailable: false
  },
  SLACK: {
    key: 'SLACK',
    order: 6,
    icon: <SlackIcon/>,
    name: 'Slack',
    description: i18n.t('Deploy your Agent on Slack', { context: 'AI agent' }),
    isPreviewAvailable: false
  },
  INSTAGRAM: {
    key: 'INSTAGRAM',
    order: 7,
    icon: <InstagramIcon />,
    name: 'Instagram',
    description: i18n.t('Deploy your Agent on Instagram', { context: 'AI agent' }),
    isPreviewAvailable: false
  },
  WHATSAPP: {
    key: 'WHATSAPP',
    order: 8,
    icon: <WhatsAppIcon />,
    name: 'WhatsApp',
    description: i18n.t('Deploy your Agent on WhatsApp', { context: 'AI agent' }),
    isPreviewAvailable: false
  },
  API: {
    key: 'API',
    order: 9,
    icon: <ApiIcon />,
    name: 'API',
    description: i18n.t('Deploy your Agent on API', { context: 'AI agent' }),
    isPreviewAvailable: false
  }
};

export const DEFAULT_WEBCHAT_DEPLOYMENT = {
  displayName: 'Preview Deployment',
  description: 'Preview deployment for testing purposes',
  platform: 'MICROSOFT_WEB_CHAT',
  isPreview: true,
  scope: 'SYSTEM',
  settings: {
    layout: {
      microsoftWebChat: {
        configuration: {
          'headerText': 'Virtual Assistant',
          'fontFamily': 'Arial,Verdana',
          'botAvatarImage': 'https://public-storage-production.s3.eu-central-1.amazonaws.com/6076d913a36bd566f862e5fc/qESuxTy-Omni.png',
          'userAvatarImage': 'User Avatar Image',
          'mainColor': '#605DEC',
          'mainFontColor': '#ffffff',
          'botBubbleColor': '#EFEFFD',
          'botBubbleTextColor': '#626988',
          'userBubbleColor': '#626988',
          'userBubbleTextColor': '#FFFFFF',
          'suggestedActionLayout': 'carousel',
          'hideUploadButton': false,
          'hideEmojiPickerButton': false,
          'showTypingIndicator': true,
          'entryPoint': '',
          'mode': 'BUBBLE',
          'menuButtons': [],
          'customSettings': null,
          'autoPopOut': {
            'enable': false,
            'delayInSeconds': 2,
            'extendTimeAfterView': false,
            'once': false
          },
          'userPromptBubble': {
            'enable': false,
            'delayInSeconds': 0,
            'message': ''
          }
        }
      }
    }
  }
};

export const SURVEY_TYPES = {
  INTERVIEW: 'INTERVIEW',
  LIVECHAT: 'LIVECHAT'
};

export const TAG_SCOPES = {
  TENANTS: 'TENANTS',
  CHAT_SESSIONS: 'CHAT_SESSIONS'
};

export const VARIABLE_TYPES = {
  AUTO: 'AUTO',
  QUESTION: 'QUESTION',
  SESSION: 'SESSION',
  CONTACT: 'CONTACT',
  CALLBACK: 'CALLBACK',
  SYSTEM: 'SYSTEM',
  LIVECHAT: 'LIVECHAT',
  CSAT: 'CSAT',
  STATIC: 'STATIC',
  CONSTANT: 'CONSTANT'
};

export const ANALYTICS_SECTIONS = {
  SUMMARY: {
    key: 'summary',
    subSections: {
      ENGAGEMENT: { key: 'engagement', shareable: true },
      SESSIONS: { key: 'sessions', shareable: true },
      USER_INTERACTIONS: { key: 'userInteractions', shareable: true },
      USERS: { key: 'users', shareable: true }
    }
  },
  AUTOMATED_ANSWERS: {
    key: 'automatedAnswers',
    subSections: {
      USAGE: { key: 'usage', shareable: true }
    }
  },
  LIVECHAT: {
    key: 'livechat',
    subSections: {
      USAGE: { key: 'usage', shareable: true },
      MISSED_CHATS: { key: 'missedChats', shareable: true },
      RESPONSE_TIME: { key: 'responseTime', shareable: true },
      DURATION: { key: 'duration', shareable: true }
    }
  },
  DECISION_TREES: {
    key: 'decisionTrees',
    subSections: {
      USAGE: { key: 'usage', shareable: true },
      ACTIVITY_FUNNELING: { key: 'activityFunneling', shareable: false }
    }
  },
  INTERVIEWS: {
    key: 'interviews',
    subSections: {
      USAGE: { key: 'usage', shareable: true },
      SUCCESSFUL_VS_FAILED: { key: 'successfulVsFailed', shareable: true },
      ACTIVITY_FUNNELING: { key: 'activityFunneling', shareable: false }
    }
  },
  USER_FEEDBACK: {
    key: 'userFeedback',
    subSections: {
      OVERALL_FEEDBACK_SCORE: { key: 'overallFeedbackScore', shareable: true },
      AUTOMATED_ANSWERS_FEEDBACK_SCORE: { key: 'automatedAnswersFeedbackScore', shareable: true },
      ACTIVITIES_FEEDBACK_SCORE: { key: 'activitiesFeedbackScore', shareable: true },
      PER_RESPONSE: { key: 'perResponse', shareable: true },
      POSITIVE_FEEDBACK_ANALYSIS: { key: 'positiveFeedbackAnalysis', shareable: true },
      NEGATIVE_FEEDBACK_ANALYSIS: { key: 'negativeFeedbackAnalysis', shareable: true },
      LIVE_CHAT_FEEDBACK_SCORE: { key: 'liveChatFeedbackScore', shareable: true },
      LIVE_CHAT_AGENTS_FEEDBACK_SCORE: { key: 'liveChatAgentsFeedbackScore', shareable: true }
    }
  },
  MISSED_QUESTIONS: {
    key: 'missedQuestions',
    subSections: {
      TOTAL_MISSED_QUESTIONS: { key: 'totalMissedQuestions', shareable: true },
      TOTAL_MISSED_QUESTIONS_PER_DAY: { key: 'totalMissedQuestionsPerDay', shareable: true },
      TOP_100_MISSED_QUESTIONS: { key: 'topHundredMissedQuestions', shareable: true }
    }
  },
  CSAT: {
    key: 'CSAT',
    subSections: {
      CSAT_OVERALL_SCORE: { key: 'CSATOverallScore', shareable: true },
      CSAT_SECTIONS_SCORE: { key: 'CSATSectionsScore', shareable: true },
      CSAT_SCORE_OVERTIME: { key: 'CSATScoreOvertime', shareable: true },
      CSAT_RESPONSES_OVERTIME: { key: 'CSATResponsesOvertime', shareable: true }
    }
  }
};

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';
export const DEFAULT_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';
export const DEFAULT_DATETIME_TIMEZONE_FORMAT = 'YYYY-MM-DD HH:mm z';
export const DATETIME_BACKEND_FORMAT = 'YYYY-MM-DDTHH:mm';
export const DATETIME_DATETIME_TIMEZONE_MILLISECONDS_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS';

export const getPresetDateRanges = (timezone) => {
  const currentTimezoneMoment = moment.tz(moment(), timezone);

  return {
    'Today': [currentTimezoneMoment.clone().startOf('day'), currentTimezoneMoment.clone().endOf('day')],
    'Yesterday': [currentTimezoneMoment.clone().subtract(1, 'days').startOf('day'), currentTimezoneMoment.clone().subtract(1, 'days').endOf('day')],
    'Last Week': [currentTimezoneMoment.clone().subtract(1, 'weeks').startOf('week').startOf('day'), currentTimezoneMoment.clone().subtract(1, 'weeks').endOf('week').endOf('day')],
    'Last Month': [currentTimezoneMoment.clone().subtract(1, 'months').startOf('month').startOf('day'), currentTimezoneMoment.clone().subtract(1, 'months').endOf('month').endOf('day')],
    'Last 7 Days': [currentTimezoneMoment.clone().subtract(7, 'days').startOf('day'), currentTimezoneMoment.clone().endOf('day')],
    'Last 30 Days': [currentTimezoneMoment.clone().subtract(30, 'days').startOf('day'), currentTimezoneMoment.clone().endOf('day')]
  };
};

export const BOT_TEMPLATE_CATEGORIES = {
  LIVECHAT: {
    label: 'Livechat'
  },
  SUPPORT: {
    label: 'Support'
  },
  FAQS: {
    label: 'FAQs'
  },
  SALES: {
    label: 'Sales'
  },
  BOOKING: {
    label: 'Booking'
  },
  PROMOTION: {
    label: 'Promotion'
  },
  ENGAGEMENT: {
    label: 'Engagement'
  },
  FEEDBACK: {
    label: 'Feedback'
  },
  ONBOARDING: {
    label: 'OnBoarding'
  },
  HR_MANAGEMENT: {
    label: 'HR Management'
  },
  INTERVIEWS: {
    label: 'Surveys'
  },
  SURVEYS: {
    label: 'Surveys'
  }
};

export const AUDIT_LOG_CATEGORIES = {
  ACCESS_CONTROL: i18n.t('Access Control'),
  CONTENT: i18n.t('Content'),
  BOT_SETTINGS: i18n.t('Agent Settings'),
  BOT_DEPLOYMENTS: i18n.t('Agent Deployments'),
  ORG_SETTINGS: i18n.t('Org Settings'),
  ORG_MANAGEMENT: i18n.t('Org Management')
};

export const INTEGRATION_GRANT_TYPES = {
  AUTHORIZATION_CODE: 'AUTHORIZATION_CODE',
  AUTHORIZATION_CODE_PKCE: 'AUTHORIZATION_CODE_PKCE'
};

// A list of custom paths for existing properties according to Microsoft Dynamics 365 KB
export const KB_INTEGRATION_PROPERTIES = [
  {
    label: i18n.t('Title'),
    name: 'title'
  },
  {
    label: i18n.t('Group'),
    name: 'group'
  },
  {
    label: i18n.t('Tags'),
    name: 'tags'
  },
  {
    label: i18n.t('Body'),
    name: 'body'
  }
];

export const BOTS_TYPES = {
  RECENT: 'recent',
  ALL: 'all'
};

export const DEFAULT_DELETE_CONFIRMATION_KEYWORD = {
  BOT: 'DELETE',
  ORGANIZATION: 'DELETE',
  DEPLOYMENT: 'DELETE'
};

export const ANALYTICS_TYPES = {
  HELVIA: 'HELVIA',
  KIBANA: 'KIBANA',
  MONGO: 'MONGO'
};

export const ANALYTICS_TYPES_PROPERTIES = [
  {
    type: ANALYTICS_TYPES.HELVIA,
    displayName: 'Helvia Dashboard',
    icon: <HelviaIcon />,
    isExternal: false
  },
  {
    type: ANALYTICS_TYPES.KIBANA,
    icon: <KibanaIcon />,
    isExternal: true
  },
  {
    type: ANALYTICS_TYPES.MONGO,
    icon: <MongoIcon />,
    isExternal: true
  }
];

export const CHAT_SESSION_MESSAGE_TYPES = {
  TEXT: 'TEXT',
  POSTBACK: 'POSTBACK',
  ATTACHMENT: 'ATTACHMENT',
  TRIGGER: 'TRIGGER'
};

export const JOB_STATUSES = {
  STARTING: 'STARTING',
  RUNNING: 'RUNNING',
  STOPPED: 'STOPPED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED'
};

export const TOPIC_MODELLING_JOB_STATUSES = {
  NEW: 'NEW',
  PENDING: 'PENDING',
  FAILED: 'FAILED',
  COMPLETED: 'COMPLETED',
  RUNNING: 'RUNNING'
};

export const OPEN_AI_COMPLETION_MODELS = {
  GPT_4_1_mini: 'gpt-4.1-mini',
  GPT_4_1: 'gpt-4.1',
  GPT_4_O: 'gpt-4o',
  GPT_4_O_MINI: 'gpt-4o-mini',
  GPT_4: 'gpt-4',
  gpt_3_5_TURBO: 'gpt-3.5-turbo'
};

export const OPEN_AI_EMBEDDING_MODELS = {
  TEXT_EMBEDDING_3_SMALL: 'text-embedding-3-small',
  TEXT_EMBEDDING_3_LARGE: 'text-embedding-3-large'
};

export const GEMINI_AI_EMBEDDING_MODELS = {
  GEMINI_EMBEDDING_001: 'gemini-embedding-001',
  GEMINI_EMBEDDING_EXP_03_07: 'gemini-embedding-exp-03-07'
};

export const GEMINI_MODELS = {
  GEMINI_2_5_PRO: 'gemini-2.5-pro',
  GEMINI_2_5_FLASH: 'gemini-2.5-flash',
  GEMINI_2_5_FLASH_LITE: 'gemini-2.5-flash-lite'
};

export const SIMILARITY_ALGORITHMS = {
  EXACT_MATCH: 'EXACT_MATCH',
  JARO_WINKLER: 'JARO_WINKLER',
  LEVENSHTEIN: 'LEVENSHTEIN'
};

export const SESSION_ANALYSIS_MODES = {
  BASIC: 'BASIC',
  EXPERT: 'EXPERT'
};

export const SESSION_ANALYSIS_EXPERT_MODE_DEFAULT_INITIAL_VALUE = {
  prompt: 'You are an AI system tasked with these tasks:\n 1. Summarise the given transcript, your summarisation should not be too long or too short.\n 2. Provide a sentiment for it, the sentiment MUST be one of the words: negative, neutral, positive.\n 3. Analyse the transcript and determine the urgency level, the urgency level MUST be one of the words: urgent, normal, low.\n 4. Analyse the transcript and determine the resolution status, the resolution status MUST be one of the words: resolved, unresolved, unclear.\n 5. Given these categories: {{classification_categories}}. Classify the given transcript into ONE or MORE categories that best represent the topic, paying extra attention to overlapping categories. If multiple categories match, return all the matches. If no clear category applies, return "Non-specified / No information".\n\n## Transcript: ##\n\n{{transcript}}\n\n### Rules:\n- You MUST return your answer in a JSON object, like this:\n  `{ "summary": "A short summary of the transcript", "sentiment": "possitive", "urgency": "urgent", "resolution": "resolved", "categories": ["category1", "category2"]}`\n- You MUST NOT answer with anything else besides the JSON object. If you answer with anything else besides the JSON object, you will be penalised.',
  temperature: 0.5,
  maxTokens: 128
};

export const LANGUAGE_DETECTION_EXPERT_MODE_DEFAULT_INITIAL_VALUE = {
  prompt: 'You are an experienced translator. Detect the language of the user\'s message following the rules below:\n### Rules ###\n- You MUST provide the language "-" if you are uncertain of what language the user\'s message.\n- You MUST provide the language "-" when the user only types known initialisms or company names\n- You MUST provide the language of the user in ISO 639 (e.g. el, en, es, pt, etc).\n- You MUST respond only with the detected language\n\n'
};

export const SESSION_ANALYSIS_FEATURES = {
  SENTIMENT: {
    POSITIVE: 'positive',
    NEGATIVE: 'negative',
    NEUTRAL: 'neutral'
  },
  RESOLUTION: {
    RESOLVED: 'resolved',
    UNCLEAR: 'unclear',
    UNRESOLVED: 'unresolved'
  },
  URGENCY: {
    LOW: 'low',
    NORMAL: 'normal',
    URGENT: 'urgent'
  }
};

export const CSAT_ANALYTICS_THRESHOLDS = {
  POSITIVE: {
    threshold: 75,
    label: 'Positive'
  },
  NEUTRAL: {
    threshold: 50,
    label: 'Neutral'
  },
  NEGATIVE: {
    threshold: 0,
    label: 'Negative'
  }
};

export const EMITTER_EVENTS = {
  NEW_NOTIFICATION: 'newNotification'
};

export const APP_NOTIFICATION_TYPES = {
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING',
  DOWNLOAD: 'DOWNLOAD',
  INFO: 'INFO',
  ERROR: 'ERROR'
};

export const SEARCH_ACTIVITY_TYPES = {
  FLOWS: 'flows',
  AUTOMATED_ANSWERS: 'automated-answers'
};

export const FLOWS_SEARCH_BY_TYPES = {
  NAME: 'name',
  CONTENT: 'content',
  ANALYTIC_TAG: 'analyticTags',
  VARIABLE: 'variable'
};

export const AUTOMATED_ANSWERS_SEARCH_BY_TYPES = {
  NAME: 'name',
  CONTENT: 'content',
  VARIABLE: 'variable'
};

export const HTTP_REQUEST_CONTENT_TYPES = {
  JSON: {
    key: 'JSON',
    label: 'JSON',
    value: 'JSON'
  },
  FORM_URLENCODED: {
    key: 'FORM_URLENCODED',
    label: 'x-www-form-urlencoded',
    value: 'x-www-form-urlencoded'
  }
};

export const HIDDEN_EMOJIS = [
  '1f351', '1f352', '1f346', '1f595', '1f32e', '1f445', '1f4a6', '1fae6', '1f51e', '1f52a', '1fa93', '1f5e1', '1f5e1-fe0f',
  '1fa96', '1f52b', '1fa78', '1f9e8', '1f9df', '2694-fe0f',
  '1f4a9', '1f92c', '1faf5', '1f44a', '1f444',
  'fe0f', '1f480', '26b0-fe0f', '1f9df-200d-2642-fe0f', '1f9df-200d-2640-fe0f', '26b1-fe0f', '2620-fe0f',
  '1f46f', '1f46f-200d-2642-fe0f', '1f46f-200d-2640-fe0f', '1f9d6', '1f9d6-200d-2640-fe0f', '1f9d6-200d-2642-fe0f'
];

export const EXCLUDED_EMOJI_IDS = [
  'peach', 'cherries', 'eggplant', 'taco', 'sweat_drops', 'tongue', 'mouth', 'underage',
  'hocho', 'dagger_knife', 'crossed_swords', 'gun', 'zombie', 'male_zombie', 'female_zombie',
  'hankey', 'middle_finger', 'face_with_symbols_on_mouth', 'facepunch', 'coffin', 'funeral_urn',
  'index_pointing_at_the_viewer', 'axe', 'drop_of_blood',
  'skull', 'skull_and_crossbones',
  'dancers', 'men-with-bunny-ears-partying', 'women-with-bunny-ears-partying',
  'person_in_steamy_room', 'man_in_steamy_room', 'woman_in_steamy_room'
];

export const JOB_TYPES = {
  DOCUMENT_SEGMENTOR: 'SDS',
  KB_GROUP: 'KB_GROUP',
  AUTO_BOT_TEST: 'AUTO_BOT_TEST'
};

export const COLOR_STATUS_TYPES = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error'
};

export const INTERACTION_TYPE_FILTERS = [
  { text: 'LLM', value: 'LLM' },
  { text: 'HTTP', value: 'HTTP' },
  { text: 'Sem Search', value: 'SEMANTIC_SEARCH' },
  { text: 'Variable', value: 'VARIABLE' },
  { text: 'Tag', value: 'TAG' }
];
